interface Data {
  [key: string]: string
}

const replaceVariables = (text:string, data:Data) => {
  let newText = text
  Object.keys(data).forEach(key => {
    const regex = new RegExp(`\\[${key}\\]`, 'ig')
    newText = newText.replace(regex, data[key])
  })
  return newText
}

export const useI18n = async (chunks?: string[] | never[]) => {
  const i18nStore = useI18NStore()
  const { lang } = useRootStore()
  const { db, loadedChunks } = storeToRefs(i18nStore)

  const localeCode = (lang:string) => {
    switch (lang) {
      case 'nl':
        return 'nl_NL'
      case 'de':
        return 'de_DE'
      case 'en':
        return 'en_GB'
      default:
        return 'en_GB'
    }
  }

  const t = (text:string, data:Data) => {
    if (!db.value[lang]?.[text]) { return ' ' } // returing '' gives an error

    let newText = db.value[lang][text]
    if (typeof newText === 'object' && data && Number(data.count) > -1) {
      newText = parseInt(data.count) === 1 ? replaceVariables(newText.one, data) : replaceVariables(newText.other, data)
      return newText
    }

    if (typeof newText === 'object') {
      return newText.one
    }

    if (typeof data !== 'undefined') {
      return replaceVariables(newText, data)
    }
    return newText
  }

  const addChunk = ({ lang, chunkName, data }: { lang: string, chunkName: string, data: Data}) => {
    if (typeof db.value[lang] === 'undefined') {
      db.value[lang] = {}
    }
    Object.keys(data).forEach(key => {
      db.value[lang][`${chunkName}/${key}`] = data[key]
    })
    loadedChunks.value.push(chunkName)
  }

  const loadChunk = async (chunkName:never | string, { lang }: { lang: string }) => {
    if (loadedChunks.value.includes(chunkName)) {
      return
    }

    if (process.server) {
      const data = await import(`../public/localize/${localeCode(lang)}/${chunkName}.json`).catch(() => {})
      if (data) {
        addChunk({
          chunkName,
          lang,
          data: data.default
        })
      }
    }

    if (process.client) {
      let result = await fetch(`/localize/${localeCode(lang)}/${chunkName}.json`).catch(() => {})
      if (!result?.ok) {
        const runtimeConfig = useRuntimeConfig()
        result = await fetch(`${runtimeConfig.public.cdnURL}storage/lang/${localeCode(lang)}/${chunkName}.json`).catch(() => {})
      }
      if (result?.ok) {
        addChunk({
          chunkName,
          lang,
          data: await result.json()
        })
      }
    }
  }

  if (chunks?.length) {
    /* eslint-disable prefer-const */
    let jobs: Promise<void>[] = []
    chunks.forEach(chunkName => {
      jobs.push(loadChunk(chunkName, { lang }))
    })
    return await Promise.all(jobs)
  }

  return { t }
}

export const $t = (text:string, data: Data | null = null) => {
  const { lang } = useRootStore()
  const i18nStore = useI18NStore()
  const { db } = storeToRefs(i18nStore)

  if (!db.value[lang]?.[text]) { return ' ' } // returing '' gives an error

  let newText = db.value[lang][text]
  if (typeof newText === 'object' && data?.count !== undefined && parseFloat(data.count) > -1) {
    newText = parseFloat(data.count) === 1 ? newText.one : newText.other
  }

  if (data) {
    return replaceVariables(newText, data)
  }
  return newText
}
